import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { ApplicationError, EditDate, Label, Show, ThemedLoader } from "Components";
import { WeeklyRotaViewModel } from "./WeeklyRotaViewModel";
import { AddWeeklyRotaModal } from "./Modal/AddWeeklyRotaModal";
import { WeeklyRotaDetailView } from "./WeeklyRotaDetail/WeeklyRotaDetailView";

import { PageContainer, PageHeader } from "Styles/PageStyles";
import {
    AddRotaButton,
    BannerContainer,
    ClickableBannerLabel,
    DateNavRow,
    ModalOverlay,
    PageHeaderLabel,
    PageWrapper,
    RotaNavButton,
    RotaPageRoot,
    Separator,
} from "./WeeklyRotaView.styles";
import { setPageTitle } from "Application";

interface WeeklyRotaViewProps {}

export const WeeklyRotaView: React.FC<WeeklyRotaViewProps> = observer(() => {
    const [viewModel] = useState(() => new WeeklyRotaViewModel());

    useEffect(() => {
        setPageTitle("Weekly Rota");

        viewModel.loadRotaByDate();
    }, []);

    return (
        <RotaPageRoot padding="0px">
            <PageHeader>
                <PageHeaderLabel fontStyle="h1" color="black">
                    Weekly Rota
                </PageHeaderLabel>
            </PageHeader>
            <PageContainer padding="0px">
                <div className="scrollable-section">
                    <Show if={viewModel.hasWeeklyRota}>
                        <SessionsLeftBanner viewModel={viewModel} />
                    </Show>
                    <PageWrapper>
                        <DateControls viewModel={viewModel} />
                        <RotaContent viewModel={viewModel} />
                    </PageWrapper>
                </div>
            </PageContainer>
        </RotaPageRoot>
    );
});

// ─── Composer sub-components ─────────────────────────────────────────────────

interface SessionsLeftBannerProps {
    viewModel: WeeklyRotaViewModel;
}

const SessionsLeftBanner: React.FC<SessionsLeftBannerProps> = observer(({ viewModel }) => (
    <BannerContainer onClick={viewModel.weeklyRotaDetailViewModel.showSessionLeftModalCommand.execute}>
        <ClickableBannerLabel fontStyle="h2">
            {viewModel.sessionsLeftCount} sessions left to assign for week commencing {viewModel.startDateDisplay}
        </ClickableBannerLabel>
    </BannerContainer>
));

interface DateControlsProps {
    viewModel: WeeklyRotaViewModel;
}

const DateControls: React.FC<DateControlsProps> = observer(({ viewModel }) => (
    <>
        <DateNavRow>
            <EditDate
                value={() => viewModel.weeklyRotaDetailViewModel.getValue("startDate")}
                command={viewModel.weeklyRotaDetailViewModel.updateRotaDateRangeCommand}
                displayName={<Label fontStyle="h2">Week Starting:</Label>}
                labelPosition="left"
                mode="date"
                disableWeekends
            />
            <RotaNavButton command={viewModel.updatePreviousDateCommand} displayName={"Previous"} paletteColor="primary" />
            <RotaNavButton command={viewModel.updateNextDateCommand} displayName={"Next"} paletteColor="primary" />
        </DateNavRow>
        <Show if={viewModel.showAddRotaSection}>
            <>
                <AddRotaButton command={viewModel.toggleWeeklyRotaModalOpenStateCommand} displayName={"ADD WEEKLY ROTA"} paletteColor="primary" />
                <Separator />
                <Show if={viewModel.isWeeklyRotaModalOpen}>
                    <AddWeeklyModal viewModel={viewModel} />
                </Show>
            </>
        </Show>
    </>
));

interface RotaContentProps {
    viewModel: WeeklyRotaViewModel;
}

const RotaContent: React.FC<RotaContentProps> = observer(({ viewModel }) => (
    <>
        <Show if={viewModel.showRotaLoader}>
            <ThemedLoader isOpen delayBeforeOpen={250} containerStyles={{ position: "absolute", top: "50%", transform: "translateY(-50%)" }} />
        </Show>
        <Show if={viewModel.hasApiError}>
            <ApplicationError additionalMessage={viewModel.apiClient.ValidationMessage} resetCommand={viewModel.resetApiClientErrorCommand} />
        </Show>
        <Show if={!viewModel.isLoading}>
            <WeeklyRotaDetailView viewModel={viewModel.weeklyRotaDetailViewModel} key={viewModel.weeklyRotaDetailViewModel.model.rotaId} />
        </Show>
    </>
));

interface AddWeeklyModalProps {
    viewModel: WeeklyRotaViewModel;
}

const AddWeeklyModal: React.FC<AddWeeklyModalProps> = ({ viewModel }) => (
    <ModalOverlay>
        <AddWeeklyRotaModal viewModel={viewModel} />
    </ModalOverlay>
);
